import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaArrowRight, FaBookOpen, FaQuestionCircle } from "react-icons/fa";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const fadeUp = (d = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.5, delay: d },
});

const ARTICLES = [
  {
    id: "medicare-101",
    title: "Medicare 101: Getting Started",
    blurb: "Simple steps for Parts A, B, C & D and when to enroll.",
    to: "/blog/medicare-101-getting-started",
  },
  {
    id: "advantage",
    title: "Medicare Advantage (Part C)",
    blurb: "HMO vs PPO, networks, extras, and common trade‑offs.",
    to: "/medicare-a",
  },
  {
    id: "supplements",
    title: "Medicare Supplement (Medigap)",
    blurb: "Plan letters, pricing, underwriting, and timing windows.",
    to: "/medicare-supplements",
  },
  {
    id: "part-d",
    title: "Part D Prescription Drug Plans",
    blurb: "How formularies work and comparing drug costs.",
    to: "/medicare-d",
  },
];

function Card({ item }) {
  return (
    <Link
      to={item.to}
      className="block rounded-2xl border border-gray-200/70 bg-white p-5 shadow-sm transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#0B5EFD]/30"
    >
      <h3 className="text-base font-semibold">{item.title}</h3>
      <p className="mt-1 text-sm text-gray-600">{item.blurb}</p>
      <span className="mt-3 inline-flex items-center text-sm font-medium text-[#0B5EFD]">Read more <FaArrowRight className="ml-2"/></span>
    </Link>
  );
}

export default function MedicareBasics() {
  return (
    <>
      <Helmet>
        <title>Medicare Basics | Your Choice Associates</title>
        <meta name="description" content="Learn Medicare basics: Parts A–D, Advantage vs. Supplement, Part D drugs, and how to get help from a licensed broker." />
      </Helmet>
      <Navbar />

      <section className="bg-gradient-to-b from-[#f7fbff] to-white">
        <div className="mx-auto max-w-7xl px-6 md:px-8 py-14">
          <motion.div {...fadeUp(0)} className="text-center">
            <span className="inline-block rounded-full bg-[#e8f1fb] px-3 py-1 text-xs font-semibold tracking-wider text-[#002B5C]">Medicare</span>
            <h1 className="mt-4 text-3xl font-extrabold text-[#002B5C] md:text-4xl">Medicare Basics</h1>
            <p className="mx-auto mt-3 max-w-2xl text-gray-600">Start here to understand Parts A–D, the difference between Advantage and Supplement plans, and how Part D works.</p>
          </motion.div>

          <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {ARTICLES.map((a) => <Card key={a.id} item={a} />)}
          </div>

          {/* Quick Steps */}
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <motion.div {...fadeUp(0.05)} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-bold text-[#002B5C] flex items-center gap-2"><FaBookOpen/> New to Medicare? Start here</h2>
              <ol className="mt-3 list-decimal pl-5 text-sm text-gray-700 space-y-2">
                <li>Enroll in Original Medicare (Parts A & B) via Social Security.</li>
                <li>Choose <strong>one</strong>: a Medicare Advantage plan <em>or</em> stay on Original + a Medigap plan.</li>
                <li>If you take prescriptions, add a Part D plan (or choose an Advantage plan that includes Rx).</li>
                <li>Talk with a broker for quotes and local network fit.</li>
              </ol>
            </motion.div>
            <motion.div {...fadeUp(0.1)} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-bold text-[#002B5C]">Get help from a licensed broker</h2>
              <p className="mt-2 text-sm text-gray-700">Our brokers compare plans across carriers and explain trade‑offs so you can decide confidently.</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Link to="/plans" className="inline-flex items-center rounded-xl bg-[#0B5EFD] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-95">Find a local broker</Link>
                <Link to="/contact" className="inline-flex items-center rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold shadow-sm hover:bg-gray-50">Ask a question</Link>
              </div>
            </motion.div>
          </div>

          {/* Mini FAQ for this topic */}
          <div className="mt-12">
            <h2 className="text-lg font-bold text-[#002B5C] flex items-center gap-2"><FaQuestionCircle/> Medicare basics — quick answers</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <p className="font-medium">What’s the difference between Advantage and Medigap?</p>
                <p className="mt-1 text-sm text-gray-700">Advantage replaces Original Medicare and manages your care through a network; Medigap keeps Original and helps pay copays/coinsurance.</p>
                <Link to="/medicare-a" className="mt-3 inline-flex items-center text-sm font-medium text-[#0B5EFD]">Learn about Advantage <FaArrowRight className="ml-2"/></Link>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <p className="font-medium">When should I enroll?</p>
                <p className="mt-1 text-sm text-gray-700">Initial Enrollment is around your 65th birthday. There are also Special and Annual Enrollment Periods.</p>
                <Link to="/blog/medicare-101-getting-started" className="mt-3 inline-flex items-center text-sm font-medium text-[#0B5EFD]">Start with Medicare 101 <FaArrowRight className="ml-2"/></Link>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <p className="font-medium">How do prescriptions work?</p>
                <p className="mt-1 text-sm text-gray-700">Part D plans have formularies and tiers. A broker can check your meds across plans.</p>
                <Link to="/medicare-d" className="mt-3 inline-flex items-center text-sm font-medium text-[#0B5EFD]">About Part D <FaArrowRight className="ml-2"/></Link>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <p className="font-medium">Can I switch later?</p>
                <p className="mt-1 text-sm text-gray-700">Yes, during eligible periods. Some changes may require underwriting.</p>
                <Link to="/medicare-supplements" className="mt-3 inline-flex items-center text-sm font-medium text-[#0B5EFD]">About Medigap <FaArrowRight className="ml-2"/></Link>
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4 text-sm">
            <Link to="/help" className="inline-flex items-center rounded-xl border border-gray-300 bg-white px-4 py-2 font-semibold shadow-sm hover:bg-gray-50">Back to Help Center</Link>
            <Link to="/faq" className="inline-flex items-center rounded-xl bg-[#0B5EFD] px-4 py-2 font-semibold text-white shadow-sm hover:opacity-95">See all FAQs</Link>
          </div>
        </div>
      </section>

      <Footer />
      <style>{`.link{color:#0B5EFD;text-decoration:underline;text-underline-offset:2px}`}</style>
    </>
  );
}
